import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Play, Mic, MicOff, RefreshCw, Eye,
  Trophy, Repeat, RotateCcw, Volume2, AlertCircle, TrendingUp, Save, Headphones, Trash2,
  Square, Sparkles, ChevronRight,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { memorizationApi, tajwidApi } from '../services/api';
import api from '../services/api';
import { useAudio } from '../context/AudioContext';
import { quranAudio } from '../audio/quranAudio';
import { memorizationAudioStorage } from '../audio/memorizationAudioStorage';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { SectionHeader, PageLoading, GlassCard } from '../components/shared';
import { toast } from 'sonner';

const STATUS_BADGE = {
  learning: { label: 'En apprentissage', color: 'text-amber-300 bg-amber-900/30' },
  needs_review: { label: 'À consolider', color: 'text-orange-300 bg-orange-900/30' },
  validated: { label: 'Validé', color: 'text-blue-300 bg-blue-900/30' },
  mastered: { label: 'Maîtrisé', color: 'text-emerald-300 bg-emerald-900/30' },
};

const WS_URL = (() => {
  const base = process.env.REACT_APP_BACKEND_URL;
  const wsBase = base.replace(/^http/, 'ws');
  return `${wsBase}/api/memorization/ws/validate`;
})();

const TOKEN_KEY = 'noor_albayan_token';

// ---------- Audio repetition controls ----------
const RepetitionPanel = ({ plan, onPlay, onStop, isPlaying, isMemoMode }) => {
  const [mode, setMode] = useState('each_ayah');
  const [repeatCount, setRepeatCount] = useState(3);
  const [pauseSec, setPauseSec] = useState(1);
  const [singleAyah, setSingleAyah] = useState(plan.from_ayah);

  const handleStart = () => {
    onPlay({
      mode,
      repeatCount,
      pauseMs: pauseSec * 1000,
      startAyah: mode === 'single_ayah' ? Number(singleAyah) : null,
    });
  };

  const ayahOptions = useMemo(() => {
    const out = [];
    for (let i = plan.from_ayah; i <= plan.to_ayah; i++) out.push(i);
    return out;
  }, [plan.from_ayah, plan.to_ayah]);

  return (
    <GlassCard className="space-y-4" data-testid="memo-repeat-panel">
      <div className="flex items-center gap-2 mb-2">
        <Repeat className="w-5 h-5 text-primary" />
        <h3 className="text-lg font-serif text-text-primary">Écoute & répétition</h3>
      </div>

      <div>
        <Label className="text-sm text-text-secondary mb-2 block">Mode de répétition</Label>
        <Select value={mode} onValueChange={setMode}>
          <SelectTrigger className="bg-background border-border" data-testid="repeat-mode-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-surface border-border">
            <SelectItem value="single_ayah">Un verset précis × N fois</SelectItem>
            <SelectItem value="each_ayah">Chaque verset × N fois (puis suivant)</SelectItem>
            <SelectItem value="full_range">Toute la plage × N fois</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {mode === 'single_ayah' && (
        <div>
          <Label className="text-sm text-text-secondary mb-2 block">Verset</Label>
          <Select value={String(singleAyah)} onValueChange={(v) => setSingleAyah(Number(v))}>
            <SelectTrigger className="bg-background border-border" data-testid="single-ayah-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-72 bg-surface border-border">
              {ayahOptions.map((a) => (
                <SelectItem key={a} value={String(a)}>Verset {a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-sm text-text-secondary">Nombre de répétitions</Label>
          <span className="text-sm text-primary font-medium" data-testid="repeat-count-label">×{repeatCount}</span>
        </div>
        <Slider
          value={[repeatCount]}
          min={1} max={20} step={1}
          onValueChange={(v) => setRepeatCount(v[0])}
          data-testid="repeat-count-slider"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-sm text-text-secondary">Pause entre répétitions</Label>
          <span className="text-sm text-primary font-medium">{pauseSec}s</span>
        </div>
        <Slider
          value={[pauseSec]}
          min={0} max={5} step={0.5}
          onValueChange={(v) => setPauseSec(v[0])}
        />
      </div>

      <div className="flex gap-2">
        {!isPlaying || !isMemoMode ? (
          <Button
            onClick={handleStart}
            className="flex-1 bg-primary hover:bg-primary/90 text-background"
            data-testid="repeat-start-btn"
          >
            <Play className="w-4 h-4 mr-2" />
            Démarrer la répétition
          </Button>
        ) : (
          <Button
            onClick={onStop}
            variant="outline"
            className="flex-1 border-danger/40 text-danger hover:bg-danger/10"
            data-testid="repeat-stop-btn"
          >
            <Square className="w-4 h-4 mr-2" />
            Arrêter
          </Button>
        )}
      </div>

      <p className="text-xs text-text-muted">
        Récitateur du plan : <span className="text-text-secondary">{plan.reciter_id}</span> ·
        Riwāya : Ḥafṣ
      </p>
    </GlassCard>
  );
};

// ---------- Validation panel (mic + WS streaming) ----------
const ValidationPanel = ({ plan, onValidationComplete, onSessionUpdate }) => {
  const [phase, setPhase] = useState('idle'); // idle | recording | processing | done | error
  const [expectedWords, setExpectedWords] = useState([]);
  const [alignment, setAlignment] = useState([]);
  const [summary, setSummary] = useState(null);
  const [finalScore, setFinalScore] = useState(null);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [error, setError] = useState(null);
  const [chunkBusy, setChunkBusy] = useState(false);
  const [saveAudio, setSaveAudio] = useState(false);
  const [savedAudioId, setSavedAudioId] = useState(null);

  const wsRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const chunkTimerRef = useRef(null);
  const streamRef = useRef(null);
  const lastSentSizeRef = useRef(0);
  const saveAudioRef = useRef(false);

  useEffect(() => { saveAudioRef.current = saveAudio; }, [saveAudio]);

  // Cleanup
  const cleanup = useCallback(() => {
    if (chunkTimerRef.current) {
      clearInterval(chunkTimerRef.current);
      chunkTimerRef.current = null;
    }
    if (recRef.current && recRef.current.state !== 'inactive') {
      try { recRef.current.stop(); } catch {}
    }
    recRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      try { wsRef.current.close(); } catch {}
    }
    wsRef.current = null;
    chunksRef.current = [];
    lastSentSizeRef.current = 0;
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const blobToBase64 = (blob) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onloadend = () => {
      const s = String(r.result || '');
      const idx = s.indexOf(',');
      resolve(idx >= 0 ? s.slice(idx + 1) : s);
    };
    r.onerror = reject;
    r.readAsDataURL(blob);
  });

  const sendCumulativeChunk = async () => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    if (!chunksRef.current.length) return;
    if (chunkBusy) return;
    const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
    if (blob.size === lastSentSizeRef.current) return;
    if (blob.size < 4096) return; // wait until we have at least ~4KB
    lastSentSizeRef.current = blob.size;
    setChunkBusy(true);
    try {
      const b64 = await blobToBase64(blob);
      wsRef.current.send(JSON.stringify({
        type: 'audio_chunk',
        audio_b64: b64,
        hints_used: hintsUsed,
      }));
    } catch (e) {
      // network or encoding error — ignore, will retry next tick
    }
  };

  const startRecording = async () => {
    setError(null);
    setAlignment([]);
    setSummary(null);
    setFinalScore(null);

    // 1) Request microphone permission (Android will show permission dialog)
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
    } catch (e) {
      if (e.name === 'NotAllowedError') {
        setError('Permission microphone refusée. Veuillez l\'autoriser dans les paramètres.');
      } else if (e.name === 'NotFoundError') {
        setError('Microphone non disponible sur cet appareil.');
      } else {
        setError('Microphone refusé ou indisponible.');
      }
      setPhase('error');
      return;
    }

    // 2) Open WebSocket and init
    const token = localStorage.getItem(TOKEN_KEY) || '';
    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'init', plan_id: plan.id, token }));
    };

    ws.onmessage = async (evt) => {
      let data;
      try { data = JSON.parse(evt.data); } catch { return; }
      if (data.type === 'ready') {
        setExpectedWords(data.expected_words || []);
        // Start MediaRecorder once WS is ready
        try {
          const rec = new MediaRecorder(stream, { mimeType: 'audio/webm' });
          recRef.current = rec;
          rec.ondataavailable = (e) => {
            if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
          };
          rec.onstop = () => {};
          rec.start(1000); // emit chunks every 1s, accumulate buffer
          setPhase('recording');
          // Send cumulative every ~4s
          chunkTimerRef.current = setInterval(sendCumulativeChunk, 4000);
        } catch (e) {
          setError('Enregistrement non supporté par ce navigateur.');
          setPhase('error');
          cleanup();
        }
      } else if (data.type === 'alignment') {
        setAlignment(data.words || []);
        setSummary(data.summary || null);
        setChunkBusy(false);
      } else if (data.type === 'busy') {
        setChunkBusy(false);
      } else if (data.type === 'final_score') {
        setFinalScore(data);
        setAlignment(data.words || []);
        setSummary({
          score: data.score, label: data.label,
          validatedWords: data.validatedWords, uncertainWords: data.uncertainWords,
          wrongWords: data.wrongWords, skippedWords: data.skippedWords,
          hintsUsed: data.hintsUsed,
        });
        setPhase('done');
        // Persist audio locally if requested
        if (saveAudioRef.current && chunksRef.current.length && data.id) {
          try {
            const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
            await memorizationAudioStorage.save({
              validationId: data.id,
              planId: plan.id,
              score: data.score,
              label: data.label,
              surahNumber: plan.surah_number,
              fromAyah: plan.from_ayah,
              toAyah: plan.to_ayah,
              transcribed: data.transcribedRaw || '',
              blob,
            });
            setSavedAudioId(data.id);
            toast.success('Audio sauvegardé localement');
          } catch (e) {
            toast.error(e.message || 'Sauvegarde audio impossible');
          }
        }
        cleanup();
        onValidationComplete && onValidationComplete(data);
        onSessionUpdate && onSessionUpdate({ validatedAyahKey: plan && `${plan.surah_number}:${plan.from_ayah}-${plan.to_ayah}` });
      } else if (data.type === 'error') {
        setError(data.detail || 'Erreur de validation');
        setPhase('error');
        cleanup();
      }
    };

    ws.onerror = () => {
      setError('Connexion WebSocket impossible.');
      setPhase('error');
      cleanup();
    };

    ws.onclose = () => {
      // Only treat as error if we weren't done
      if (phase !== 'done') {
        if (chunkTimerRef.current) clearInterval(chunkTimerRef.current);
        chunkTimerRef.current = null;
      }
    };
  };

  const finalize = async () => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    setPhase('processing');
    if (recRef.current && recRef.current.state !== 'inactive') {
      try { recRef.current.stop(); } catch {}
    }
    if (chunkTimerRef.current) {
      clearInterval(chunkTimerRef.current);
      chunkTimerRef.current = null;
    }
    // Wait briefly for the last chunk(s) to flush, then send a final cumulative
    await new Promise((r) => setTimeout(r, 600));
    await sendCumulativeChunk();
    // Give Whisper time before finalize
    await new Promise((r) => setTimeout(r, 200));
    try {
      wsRef.current.send(JSON.stringify({ type: 'finalize' }));
    } catch {}
  };

  const reset = () => {
    cleanup();
    setPhase('idle');
    setAlignment([]);
    setSummary(null);
    setFinalScore(null);
    setHintsUsed(0);
    setError(null);
    setExpectedWords([]);
  };

  // Visible word renderer: keep page empty initially, reveal as recognized
  const revealedWords = useMemo(() => {
    if (!alignment.length) return [];
    return alignment.map((w, i) => ({
      ...w,
      idx: i,
      ayah_key: w.ayah_key,
    }));
  }, [alignment]);

  // Group revealed words by ayah for nicer rendering
  const groupedByAyah = useMemo(() => {
    const groups = {};
    revealedWords.forEach((w) => {
      const k = w.ayah_key || 'unknown';
      groups[k] = groups[k] || [];
      groups[k].push(w);
    });
    return groups;
  }, [revealedWords]);

  const wordClass = (status) => {
    if (status === 'validated') return 'text-emerald-300';
    if (status === 'uncertain') return 'text-amber-300';
    if (status === 'wrong') return 'text-red-300';
    return 'text-text-muted/30';
  };

  const isIdle = phase === 'idle';
  const isRecording = phase === 'recording';
  const isProcessing = phase === 'processing';
  const isDone = phase === 'done';

  return (
    <GlassCard className="space-y-5" data-testid="memo-validation-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Mic className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-serif text-text-primary">Validation par récitation</h3>
        </div>
        {summary && (
          <div className="text-right">
            <div className="text-2xl font-serif text-primary" data-testid="memo-live-score">
              {summary.score}/100
            </div>
            <div className="text-xs text-text-muted">{summary.label}</div>
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-md bg-danger/10 border border-danger/30 text-danger text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Progressive words display — empty until validated */}
      <div
        className="min-h-[160px] p-5 rounded-lg bg-background/60 border border-border"
        dir="rtl"
        data-testid="memo-words-display"
      >
        {isIdle && (
          <div className="text-center py-8" dir="ltr">
            <Mic className="w-12 h-12 text-primary/40 mx-auto mb-3" />
            <p className="text-text-secondary text-sm">
              Cliquez sur « Démarrer » et récitez la portée à mémoriser. Les mots apparaîtront
              au fur et à mesure que la transcription les valide.
            </p>
          </div>
        )}
        {!isIdle && Object.keys(groupedByAyah).length === 0 && (
          <div className="text-center py-8" dir="ltr">
            <div className="inline-flex items-center gap-2 text-text-muted text-sm">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              Écoute en cours...
            </div>
          </div>
        )}
        {Object.entries(groupedByAyah).map(([ayahKey, words]) => (
          <div key={ayahKey} className="mb-4 last:mb-0">
            <div className="text-xs text-text-muted mb-1" dir="ltr">{ayahKey}</div>
            <div className="font-arabic text-2xl md:text-3xl leading-loose flex flex-wrap gap-x-3 gap-y-2 justify-end">
              {words.map((w) => (
                <span
                  key={w.idx}
                  className={`transition-colors duration-300 ${wordClass(w.status)}`}
                  data-testid={`memo-word-${w.idx}`}
                  data-status={w.status}
                  title={`${w.status} · ${w.expected}`}
                >
                  {w.status === 'missing' && !isDone ? '…' : w.expected}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Save audio toggle (only meaningful before starting) */}
      {isIdle && (
        <div className="flex items-center justify-between p-3 rounded-md bg-surface-elevated/50 border border-border">
          <div className="flex-1">
            <Label htmlFor="save-audio-switch" className="text-sm text-text-primary cursor-pointer">
              Sauvegarder mon audio (sur cet appareil)
            </Label>
            <p className="text-xs text-text-muted mt-1">
              Stocké localement dans votre navigateur. Max 10 MB par récitation.
            </p>
          </div>
          <Switch
            id="save-audio-switch"
            checked={saveAudio}
            onCheckedChange={setSaveAudio}
            data-testid="memo-save-audio-switch"
          />
        </div>
      )}

      {/* Hint button (shows full text temporarily) */}
      {!isIdle && expectedWords.length > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setHintsUsed((h) => h + 1)}
          className="text-text-muted hover:text-primary"
          data-testid="memo-hint-btn"
        >
          <Eye className="w-4 h-4 mr-2" />
          J'ai eu besoin d'un indice ({hintsUsed})
        </Button>
      )}

      {/* Controls */}
      <div className="flex flex-wrap gap-2">
        {isIdle && (
          <Button
            onClick={startRecording}
            className="flex-1 bg-primary hover:bg-primary/90 text-background"
            data-testid="memo-rec-start-btn"
          >
            <Mic className="w-4 h-4 mr-2" />
            Démarrer la récitation
          </Button>
        )}
        {isRecording && (
          <>
            <Button
              onClick={finalize}
              className="flex-1 bg-emerald-700 hover:bg-emerald-600 text-white"
              data-testid="memo-rec-finalize-btn"
            >
              <Trophy className="w-4 h-4 mr-2" />
              Terminer & calculer le score
            </Button>
            <Button
              onClick={() => { cleanup(); setPhase('idle'); }}
              variant="outline"
              className="border-danger/40 text-danger hover:bg-danger/10"
              data-testid="memo-rec-cancel-btn"
            >
              <MicOff className="w-4 h-4 mr-2" />
              Annuler
            </Button>
          </>
        )}
        {isProcessing && (
          <Button disabled className="flex-1" data-testid="memo-rec-processing-btn">
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            Calcul du score...
          </Button>
        )}
        {(isDone || phase === 'error') && (
          <Button
            onClick={reset}
            variant="outline"
            className="flex-1 border-primary/40 text-primary hover:bg-primary/10"
            data-testid="memo-rec-reset-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Nouvelle tentative
          </Button>
        )}
      </div>

      {finalScore && (
        <div className="space-y-2 pt-3 border-t border-border" data-testid="memo-final-summary">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <div className="p-2 rounded bg-emerald-900/20 border border-emerald-800/40">
              <div className="text-emerald-300 font-medium">{finalScore.validatedWords}</div>
              <div className="text-text-muted">Validés</div>
            </div>
            <div className="p-2 rounded bg-amber-900/20 border border-amber-800/40">
              <div className="text-amber-300 font-medium">{finalScore.uncertainWords}</div>
              <div className="text-text-muted">Incertains</div>
            </div>
            <div className="p-2 rounded bg-red-900/20 border border-red-800/40">
              <div className="text-red-300 font-medium">{finalScore.wrongWords}</div>
              <div className="text-text-muted">Erronés</div>
            </div>
            <div className="p-2 rounded bg-surface-elevated border border-border">
              <div className="text-text-secondary font-medium">{finalScore.skippedWords}</div>
              <div className="text-text-muted">Manquants</div>
            </div>
          </div>
          {savedAudioId && (
            <SavedAudioPlayer validationId={savedAudioId} />
          )}
          <p className="text-xs text-text-muted italic">
            {finalScore.validation_disclaimer}
          </p>
        </div>
      )}
    </GlassCard>
  );
};

// Local saved audio mini-player
const SavedAudioPlayer = ({ validationId }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoked;
    memorizationAudioStorage.getObjectUrl(validationId).then((u) => {
      setUrl(u);
      revoked = u;
    });
    return () => {
      if (revoked) try { URL.revokeObjectURL(revoked); } catch {}
    };
  }, [validationId]);
  if (!url) return null;
  return (
    <div className="flex items-center gap-2 p-2 rounded bg-surface-elevated/50 border border-border">
      <Headphones className="w-4 h-4 text-primary flex-shrink-0" />
      <span className="text-xs text-text-secondary flex-shrink-0">Votre récitation :</span>
      <audio src={url} controls className="flex-1 h-8" data-testid="memo-saved-audio-player" />
    </div>
  );
};

// ---------- Score evolution sparkline (last N validations) ----------
const ScoreSparkline = ({ history }) => {
  if (!history || history.length < 2) {
    return (
      <p className="text-xs text-text-muted">
        Pas encore assez de validations pour afficher la courbe (minimum 2).
      </p>
    );
  }
  const ordered = [...history].reverse(); // oldest → newest
  const W = 600;
  const H = 100;
  const PAD = 8;
  const max = 100;
  const min = 0;
  const stepX = (W - 2 * PAD) / Math.max(1, ordered.length - 1);
  const points = ordered.map((v, i) => {
    const x = PAD + i * stepX;
    const y = PAD + (H - 2 * PAD) * (1 - (v.score - min) / (max - min));
    return [x, y, v];
  });
  const path = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  const last = points[points.length - 1];
  return (
    <div className="w-full overflow-hidden">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-24" data-testid="memo-score-sparkline">
        {/* baseline 70 (success threshold) */}
        <line
          x1={PAD}
          x2={W - PAD}
          y1={PAD + (H - 2 * PAD) * (1 - 70 / 100)}
          y2={PAD + (H - 2 * PAD) * (1 - 70 / 100)}
          stroke="rgba(255,255,255,0.08)"
          strokeDasharray="3 3"
        />
        <path d={path} fill="none" stroke="hsl(45 95% 60%)" strokeWidth="2" />
        {points.map(([x, y, v], i) => (
          <g key={i}>
            <circle cx={x} cy={y} r="3" fill="hsl(45 95% 60%)" />
          </g>
        ))}
        <text x={last[0]} y={Math.max(12, last[1] - 6)} fill="hsl(45 95% 60%)" fontSize="11" textAnchor="middle">
          {last[2].score}
        </text>
      </svg>
      <p className="text-xs text-text-muted text-center">
        Évolution de votre score sur les {ordered.length} dernières validations
      </p>
    </div>
  );
};

// ---------- Tajwīd summary card ----------
const TajweedRulesSummary = ({ planId }) => {
  const [data, setData] = useState(null);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!planId) return;
    let cancel = false;
    setLoading(true);
    Promise.all([
      api.get(`/memorization/plans/${planId}/tajweed-summary`)
        .then((r) => r.data).catch(() => null),
      tajwidApi.getRules().then((r) => r.data || []).catch(() => []),
    ]).then(([sum, rls]) => {
      if (cancel) return;
      setData(sum);
      setRules(rls);
    }).finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [planId]);

  if (loading || !data || !data.rulesPresent?.length) return null;

  const ruleLabel = (code) => rules.find((r) => r.rule === code)?.label_fr || code;
  const accuracyByRule = Object.fromEntries(
    (data.userAccuracyPerRule || []).map((r) => [r.ruleId, r])
  );

  // Weak rules = those with accuracy < 0.7 in user history (or never practiced)
  const weak = data.rulesPresent.filter((r) => {
    const acc = accuracyByRule[r.ruleId];
    return !acc || (acc.attempts >= 1 && acc.accuracy < 0.7);
  });

  return (
    <GlassCard data-testid="memo-tajweed-summary">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-serif text-text-primary">Règles Tajwīd dans ce passage</h3>
        </div>
        <span className="text-xs text-text-muted">
          {data.totalTajweedAnnotations} occurrences · {data.rulesPresent.length} règles
        </span>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {data.rulesPresent.map((r) => {
          const acc = accuracyByRule[r.ruleId];
          const isWeak = !acc || (acc.attempts >= 1 && acc.accuracy < 0.7);
          return (
            <Link
              key={r.ruleId}
              to={`/cours/tajwid/${r.ruleId}`}
              className={`text-xs px-2 py-1 rounded border transition-colors ${
                isWeak
                  ? 'border-rose-500/50 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20'
                  : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20'
              }`}
              data-testid={`memo-tajweed-rule-${r.ruleId}`}
            >
              {ruleLabel(r.ruleId)} · {r.count}
            </Link>
          );
        })}
      </div>

      {weak.length > 0 && (
        <div className="text-xs text-rose-300 flex items-start gap-2">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            {weak.length} règle{weak.length > 1 ? 's' : ''} à consolider avant de valider :{' '}
            {weak.slice(0, 3).map((r) => ruleLabel(r.ruleId)).join(', ')}
            {weak.length > 3 ? '…' : ''}
          </span>
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <Link
          to={weak[0] ? `/cours/tajwid/${weak[0].ruleId}` : '/cours/tajwid'}
          data-testid="memo-tajweed-practice"
        >
          <Button size="sm" variant="outline">
            Pratiquer le tajwīd
            <ChevronRight className="w-3 h-3 ml-1" />
          </Button>
        </Link>
        <Link to="/mes-progres" data-testid="memo-tajweed-progress-link">
          <Button size="sm" variant="ghost" className="text-xs">
            Voir mes progrès
          </Button>
        </Link>
      </div>
    </GlassCard>
  );
};

// ---------- Page ----------
export default function MemorizationSession() {
  const { user, loading: authLoading } = useAuth();
  const { planId } = useParams();
  const navigate = useNavigate();
  const { state: audioState, stop: stopAudio } = useAudio();

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const sessionIdRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const r = await memorizationApi.getPlan(planId);
      setPlan(r.data);
    } catch (e) {
      toast.error('Plan introuvable');
      navigate('/memorization');
    } finally {
      setLoading(false);
    }
  }, [planId, navigate]);

  const refreshHistory = useCallback(async () => {
    try {
      const r = await memorizationApi.history(50);
      const planHistory = (r.data || []).filter((h) => h.plan_id === planId);
      setHistory(planHistory);
    } catch {
      // silent
    }
  }, [planId]);

  useEffect(() => {
    if (user) {
      refresh();
      refreshHistory();
    }
  }, [user, refresh, refreshHistory]);

  // Auto-start a session on mount, end it on unmount
  useEffect(() => {
    if (!user || !planId) return;
    let active = true;
    (async () => {
      try {
        const r = await memorizationApi.startSession(planId);
        if (active) sessionIdRef.current = r.data.id;
      } catch {
        // ignore: session is best-effort
      }
    })();
    return () => {
      active = false;
      const sid = sessionIdRef.current;
      if (!sid) return;
      // Best-effort end: mark notes with timestamp
      memorizationApi.updateSession(sid, { notes: 'auto-ended' }).catch(() => {});
    };
  }, [user, planId]);

  const handlePlay = (cfg) => {
    if (!plan) return;
    // Pin the reciter for the session (no switch mid-session)
    quranAudio.setReciter(plan.reciter_id || 'alafasy');
    quranAudio.playMemorizationRepeat({
      surahNumber: plan.surah_number,
      fromAyah: plan.from_ayah,
      toAyah: plan.to_ayah,
      mode: cfg.mode,
      repeatCount: cfg.repeatCount,
      pauseMs: cfg.pauseMs,
      startAyah: cfg.startAyah,
    });
  };

  const handleStop = () => stopAudio();

  if (authLoading || loading) return <PageLoading />;
  if (!user) return <Navigate to={`/login?next=/memorization/${planId}`} replace />;
  if (!plan) return null;

  const status = STATUS_BADGE[plan.memorization_status] || STATUS_BADGE.learning;
  const isMemoMode = audioState.mode === 'memorization';

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-32" data-testid="memo-session">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/memorization')}
        className="text-text-muted hover:text-text-primary mb-4"
        data-testid="memo-session-back-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Retour
      </Button>

      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <span className="overline">Plan de mémorisation</span>
          <h1 className="text-3xl sm:text-4xl font-serif text-text-primary mt-1">{plan.title}</h1>
          <p className="text-sm text-text-secondary mt-1">
            Sourate {plan.surah_number} · versets {plan.from_ayah}–{plan.to_ayah}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className={`text-xs px-2 py-1 rounded ${status.color}`} data-testid="memo-status-badge">
            {status.label}
          </span>
          {plan.best_score != null && (
            <span className="text-xs text-text-muted flex items-center gap-1">
              <Trophy className="w-3.5 h-3.5 text-primary" /> Meilleur : {plan.best_score}/100
            </span>
          )}
        </div>
      </div>

      <Tabs defaultValue="repetition" className="space-y-4">
        <TabsList className="bg-surface border border-border">
          <TabsTrigger value="repetition" data-testid="tab-repetition">
            <Volume2 className="w-4 h-4 mr-2" />
            Écoute & répétition
          </TabsTrigger>
          <TabsTrigger value="validation" data-testid="tab-validation">
            <Mic className="w-4 h-4 mr-2" />
            Validation micro
          </TabsTrigger>
        </TabsList>

        <TabsContent value="repetition" className="space-y-4">
          <RepetitionPanel
            plan={plan}
            onPlay={handlePlay}
            onStop={handleStop}
            isPlaying={audioState.isPlaying}
            isMemoMode={isMemoMode}
          />
          <TajweedRulesSummary planId={planId} />
          {audioState.needsDownload && (
            <div className="p-4 rounded-lg bg-amber-900/20 border border-amber-800/40 text-amber-200 text-sm">
              Cette sourate doit être téléchargée pour la lecture hors-ligne. Le téléchargement va
              démarrer automatiquement.
            </div>
          )}
        </TabsContent>

        <TabsContent value="validation" className="space-y-4">
          <ValidationPanel
            plan={plan}
            onValidationComplete={() => { refresh(); refreshHistory(); }}
            onSessionUpdate={() => {
              const sid = sessionIdRef.current;
              if (!sid) return;
              memorizationApi.updateSession(sid, {
                validated_ayah_keys: Array.from({ length: plan.to_ayah - plan.from_ayah + 1 },
                  (_, i) => `${plan.surah_number}:${plan.from_ayah + i}`),
              }).catch(() => {});
            }}
          />
          {history.length >= 2 && (
            <GlassCard data-testid="memo-history-chart">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-serif text-text-primary">Évolution du score</h3>
              </div>
              <ScoreSparkline history={history} />
            </GlassCard>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
